import { Button, message } from 'antd';
import { Formik } from 'formik';
import { set } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { type OptionsBlockSpecsFragment } from '../../../../api/fragments/OptionsBlockSpecs';
import {
    ContentRefinementSourceAction,
    ContentRefinementSourceKind,
    type UploadedFileWithPreview,
} from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import Form from '../../../../components/fields/Form';
import FormAutoTouch from '../../../../components/FormAutoTouch';
import hasPermissions from '../../../../utilities/hasPermissions';
import useDebounce from '../../../../utilities/useDebounce';
import useHandleError from '../../../../utilities/useHandleError';
import { type VariantConfiguratorDetailsPageProps } from '../utils';
import { useVariantConfigurator } from '../VariantConfiguratorContext';
import VariantConfiguratorLayout from '../VariantConfiguratorLayout';
import OptionsBlockForm from './OptionsBlockForm';
import useHandleBlockSave from './useHandleBlockSave';
import useOptionsBlockValidator from './useOptionsBlockValidator';
import {
    getOptionBlockInput,
    FORM_NAME,
    type OptionBlockFormValues,
    mapOptionSettings,
    type OptionBlockValue,
} from './utils';

const optionBlockSorter = (a: OptionsBlockSpecsFragment, b: OptionsBlockSpecsFragment) => a.order - b.order;

const updateOrder = (optionBlocks: OptionBlockValue[]) =>
    optionBlocks.map((value, index) => set('optionsBlockInput.order', index, value));

const OptionsBlockTab = ({ activeTab, replaceTab, disabled = false }: VariantConfiguratorDetailsPageProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);
    const { variantConfigurator, refetch } = useVariantConfigurator();

    const validator = useOptionsBlockValidator();

    const handleBlockSave = useHandleBlockSave();

    const currentOptionsBlocks = useMemo(
        () => [...variantConfigurator.optionsBlocks],
        [variantConfigurator.optionsBlocks]
    );

    const getInitialValues = useCallback((blocks?: OptionsBlockSpecsFragment[]): OptionBlockFormValues => {
        if (blocks?.length > 0) {
            return {
                optionsBlocks: blocks.toSorted(optionBlockSorter).map((block, index) => ({
                    id: block.id,
                    type: block.type,
                    optionsBlockInput: {
                        financing: block.financing,
                        isActive: block.isActive,
                        optionSettings: mapOptionSettings(block.optionSettings),
                        sectionDescription: block.sectionDescription,
                        sectionTitle: block.sectionTitle,
                        sectionImages: block.sectionImages as UploadedFileWithPreview[],
                        order: block.order || index,
                    },
                })),
            };
        }

        return { optionsBlocks: [getOptionBlockInput()] };
    }, []);

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(variantConfigurator.permissions, [
                permissionKind.updateConfigurator,
            ]),
            source: {
                kind: ContentRefinementSourceKind.Configurator,
                action: ContentRefinementSourceAction.Update,
                identifier: variantConfigurator.modelConfigurator.id,
            },
        }),
        [variantConfigurator]
    );

    const onSubmit = useDebounce(
        useHandleError<OptionBlockFormValues>(
            async (values, { resetForm }) => {
                const { optionsBlocks } = values;
                message.loading({
                    content: t('variantConfiguratorDetails:messages.options.saveSubmitting'),
                    key: 'primary',
                    duration: 0,
                });

                await handleBlockSave(updateOrder(optionsBlocks), variantConfigurator.id, currentOptionsBlocks);

                message.success({
                    content: t('variantConfiguratorDetails:messages.options.saveSuccessful'),
                    key: 'primary',
                });

                const updatedVariantConfigurator = await refetch();

                resetForm({
                    values: getInitialValues(updatedVariantConfigurator?.optionsBlocks),
                });
            },
            [currentOptionsBlocks, getInitialValues, handleBlockSave, refetch, t, variantConfigurator.id]
        )
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <VariantConfiguratorLayout
                activeTab={activeTab}
                footer={[
                    !disabled && (
                        <Button key="save" form={FORM_NAME} htmlType="submit" type="primary">
                            {t('variantConfiguratorDetails:actions.options.save')}
                        </Button>
                    ),
                ]}
                replaceTab={replaceTab}
            >
                <Formik<OptionBlockFormValues>
                    initialValues={getInitialValues(currentOptionsBlocks)}
                    onSubmit={onSubmit}
                    validate={validator}
                >
                    {({ handleSubmit }) => (
                        <Form id={FORM_NAME} name={FORM_NAME} onSubmitCapture={handleSubmit}>
                            <FormAutoTouch />
                            <OptionsBlockForm disabled={disabled} />
                        </Form>
                    )}
                </Formik>
            </VariantConfiguratorLayout>
        </GenericStateOnPageContextProvider>
    );
};

export default OptionsBlockTab;
